import { Component, computed, inject, input, InputSignal, Signal } from '@angular/core';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { ShineMigrationType } from '@shared/enums/shine-migration-type.enum';
import { ConfigurationService } from '@shared/services/configuration.service';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NgClass } from '@angular/common';
import { GetTokenShineService } from '../../services/get-token-shine.service';
import { take, tap } from 'rxjs';
import { HxNotificationsService } from '@hubbix/common-components';

interface MigrationBannerConfig {
  title: string;
  message: string;
  buttonLabel: string;
  url: string;
  secured: boolean;
}

const MIGRATION_BANNER_CONFIG: Partial<Record<ShineMigrationType, MigrationBannerConfig>> = {
  1: {
    title: 'Migration disponible pour votre solution',
    message:
      "Les solutions EBP Hubbix s'arrêtent le 31 août 2026. EBP Open Line Plus prend le relais : vos données sont préservées et votre gestion continue sans interruption tout en étant conforme à la RFE.",
    buttonLabel: 'Je découvre EBP Open Line Plus',
    url: 'https://infos.ebp.com/migration-hubbix-vers-ol-plus',
    secured: false,
  },
  2: {
    title: 'Migration disponible pour votre solution',
    message:
      "Les solutions EBP Hubbix s'arrêtent le 31 août 2026. Shine Facture prend le relais : vos données sont reprises automatiquement en 3 clics, sans ressaisie, sans interruption.",
    buttonLabel: 'Je découvre Shine Facture',
    url: 'FROM_CONFIG',
    secured: true,
  },
  3: {
    title: 'Migration disponible pour votre solution',
    message:
      "Les solutions EBP Hubbix s'arrêtent le 31 août 2026. Shine Facture prend le relais : vos données sont reprises automatiquement en 3 clics, sans ressaisie, sans interruption.",
    buttonLabel: 'Je découvre Shine Facture',
    url: 'FROM_CONFIG',
    secured: true,
  },
  4: {
    title: 'Migration disponible pour votre solution',
    message:
      "Les solutions EBP Hubbix s'arrêtent le 31 août 2026. Shine Facture prend le relais : vos données sont reprises automatiquement en 3 clics, sans ressaisie, sans interruption.",
    buttonLabel: 'Je découvre Shine Facture',
    url: 'FROM_CONFIG',
    secured: true,
  },
};

@Component({
  selector: 'app-informations',
  templateUrl: './informations.component.html',
  styleUrls: ['./informations.component.less'],
  standalone: true,
  imports: [NzAlertComponent, NzTooltipDirective, NgClass],
  providers: [GetTokenShineService],
})
export class InformationsComponent {
  private readonly configService: ConfigurationService = inject(ConfigurationService);
  private readonly getTokenForShineService: GetTokenShineService = inject(GetTokenShineService);
  private readonly notificationService: HxNotificationsService = inject(HxNotificationsService);

  public readonly shineMigrationType: InputSignal<ShineMigrationType | undefined> = input<
    ShineMigrationType | undefined
  >();

  public readonly isAuthorized: InputSignal<boolean> = input<boolean>(false);
  public readonly domainId: InputSignal<string> = input<string>('');

  protected readonly tooltip: Signal<string> = computed(() => {
    return !this.isAuthorized()
      ? 'Uniquement les utilisateurs avec les droits administrateurs ou expert-comptable peuvent réaliser la migration du domaine'
      : '';
  });

  protected readonly bannerConfig: Signal<MigrationBannerConfig | undefined> = computed(() => {
    const shineMigrationType: ShineMigrationType | undefined = this.shineMigrationType();

    if (shineMigrationType === undefined || shineMigrationType === ShineMigrationType.None) {
      return undefined;
    }

    return MIGRATION_BANNER_CONFIG[shineMigrationType];
  });

  protected readonly shouldDisplayBanner: Signal<boolean> = computed(() => {
    const config: MigrationBannerConfig | undefined = this.bannerConfig();

    if (!config) {
      return false;
    }

    const now: Date = new Date();

    if (this.shineMigrationType() === ShineMigrationType.OL) {
      const startDate: Date = this.configService.shineMigrationOlStartDate
        ? this.parseDate(this.configService.shineMigrationOlStartDate)
        : new Date(2026, 5, 15); // 15 juin 2026 par défaut
      return now >= startDate;
    }

    const startDate: Date = this.configService.shineMigrationOtherStartDate
      ? this.parseDate(this.configService.shineMigrationOtherStartDate)
      : new Date(2026, 6, 1, 14, 0, 0); // 1er juillet 2026 à 14h (heure française)
    return now >= startDate;
  });

  protected onRedirectUrl(): void {
    if (!this.isAuthorized()) {
      return;
    }

    if (!this.bannerConfig()?.secured) {
      window.open(this.bannerConfig()?.url, '_blank', 'noopener,noreferrer');
      return;
    }

    this.getTokenForShineService
      .getTokenForShineRedirection(this.domainId())
      .pipe(
        take(1),
        tap((res: { token: string }) => {
          window.open(
            `${this.configService.shineMigrationFactureEbpUrl}?token=${res.token}&utm_source=hubbix&utm_medium=migration`,
            '_blank',
            'noopener,noreferrer',
          );
        }),
      )
      .subscribe();
  }

  private parseDate(dateStr: string): Date {
    // Handle ISO 8601 datetime format (e.g., "2026-07-01T11:30:00.000Z")
    if (dateStr.includes('T')) {
      return new Date(dateStr);
    }

    // Handle simple date format (e.g., "2026-07-01") - set to midnight local time
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
}
